import {
  ADPCredentialsExist,
  CoreEntityDomainsOrganizationsEntrypointsApiV1SchemasResponseHRISProvider as HRISProviderDto,
} from '@electricjs/core_entity-client';

import { apiSlice } from '@/redux/slices/apiSlice';
import { TagTypes } from '@/redux/slices/apiSlice';
import {
  CreateMergeHrisIntegrationArgs,
  CreateLinkTokenArgs,
  CreateLinkTokenResponse,
  ManageHrisIntegrationArgs,
  SyncHrisIntegrationV2Args,
  UpdateMergeHrisIntegrationArgs,
  SyncHRISEmployeeArgs,
  CreateHrisIntegrationV2Args,
  GetSyncStatusArgs,
  SyncStatusResponse,
} from '@/types/hrisIntegration';

// Import DTO types directly from core entity client
import {
  HRISIntegrationSyncResponse,
  TriggerOrganizationSyncRequest,
  WorkflowStatus,
} from '@electricjs/core_entity-client';

import {
  invalidatesList,
  invalidatesListAndById,
  providesListAndAllById,
} from './helpers/cacheTagHelpers';
import { HRISIntegration } from '@/types/organizations';

export const hrisIntegrationApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    createLinkToken: builder.mutation<string, CreateLinkTokenArgs>({
      query: ({ organizationId, integration }) => {
        return {
          url: `/v1/organizations/${organizationId}/hris_integrations/link_token`,
          method: 'POST',
          body: {
            integration,
          },
        };
      },
      transformResponse: (response: CreateLinkTokenResponse) => {
        return response.link_token;
      },
    }),
    createMergeHrisIntegration: builder.mutation<
      void,
      CreateMergeHrisIntegrationArgs
    >({
      query: ({ organizationId, publicToken }) => ({
        url: `/v1/organizations/${organizationId}/hris_integrations`,
        method: 'POST',
        body: {
          public_token: publicToken,
        },
      }),
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.Organization, args.organizationId),
    }),
    createHrisIntegrationV2: builder.mutation<
      HRISIntegration,
      CreateHrisIntegrationV2Args
    >({
      query: ({ organizationId, providerData, providerSlug }) => ({
        url: `/v2/organizations/${organizationId}/hris_integrations`,
        method: 'POST',
        body: {
          provider_data: providerData,
          hris_provider_slug: providerSlug,
        },
      }),
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.Organization, args.organizationId),
    }),
    updateMergeHrisIntegration: builder.mutation<
      void,
      UpdateMergeHrisIntegrationArgs
    >({
      query: ({ organizationId, publicToken, hrisIntegrationId }) => ({
        url: `/v1/organizations/${organizationId}/hris_integrations/${hrisIntegrationId}`,
        method: 'PATCH',
        body: {
          public_token: publicToken,
        },
      }),
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.Organization, args.organizationId),
    }),
    deleteMergeHrisIntegration: builder.mutation<
      void,
      ManageHrisIntegrationArgs
    >({
      query: ({ organizationId, hrisIntegrationId }) => ({
        url: `/v1/organizations/${organizationId}/hris_integrations/${hrisIntegrationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.Organization, args.organizationId),
    }),
    deleteHrisIntegrationV2: builder.mutation<void, ManageHrisIntegrationArgs>({
      query: ({ organizationId, hrisIntegrationId }) => ({
        url: `/v2/organizations/${organizationId}/hris_integrations/${hrisIntegrationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.Organization, args.organizationId),
    }),
    disconnectHrisIntegration: builder.mutation<
      void,
      ManageHrisIntegrationArgs
    >({
      query: ({ organizationId, hrisIntegrationId }) => ({
        url: `/v2/organizations/${organizationId}/hris_integrations/${hrisIntegrationId}/disconnect`,
        method: 'POST',
      }),
      invalidatesTags: (_results, _error, args) => [
        ...invalidatesListAndById(TagTypes.Organization, args.organizationId),
        TagTypes.Application,
      ],
    }),
    syncHrisIntegration: builder.mutation<void, ManageHrisIntegrationArgs>({
      query: ({ organizationId, hrisIntegrationId }) => ({
        url: `/v1/organizations/${organizationId}/hris_integrations/${hrisIntegrationId}/sync`,
        method: 'POST',
      }),
      invalidatesTags: (_results, _error, args) => {
        return [
          ...invalidatesListAndById(TagTypes.Organization, args.organizationId),
          TagTypes.Employee,
          TagTypes.EmployeeWithDevice,
        ];
      },
    }),
    syncHrisIntegrationV2: builder.mutation<string, SyncHrisIntegrationV2Args>({
      query: ({ organizationId, hrisIntegrationId, filterByExternalIds }) => {
        const body: TriggerOrganizationSyncRequest = {
          filter_by_external_ids: filterByExternalIds,
        };
        return {
          url: `/v2/organizations/${organizationId}/hris_integrations/${hrisIntegrationId}/sync`,
          method: 'POST',
          body,
        };
      },
      transformResponse: (response: HRISIntegrationSyncResponse) =>
        response.workflow_id,
      invalidatesTags: (_results, _error, args) => {
        return [
          ...invalidatesListAndById(TagTypes.Organization, args.organizationId),
          TagTypes.Employee,
          TagTypes.EmployeeWithDevice,
        ];
      },
    }),
    listHrisProviders: builder.query<HRISProviderDto[], void>({
      query: () => ({
        url: '/v1/hris_providers',
        method: 'GET',
      }),
      providesTags: providesListAndAllById(TagTypes.HrisProvider),
    }),
    demoSyncHRISEmployee: builder.mutation<void, SyncHRISEmployeeArgs>({
      query: ({
        organizationId,
        hrisProviderSlug,
        hrisEmployee,
        isOnboarding,
        isOffboarding,
      }) => ({
        url: `/v1/organizations/${organizationId}/hris_integrations/demo_sync_employee`,
        method: 'POST',
        body: {
          hris_provider_slug: hrisProviderSlug,
          hris_employee: {
            source_id: hrisEmployee.sourceId,
            remote_id: hrisEmployee.remoteId || '',
            first_name: hrisEmployee.firstName,
            last_name: hrisEmployee.lastName,
            job_title: hrisEmployee.jobTitle || '',
            work_email: hrisEmployee.workEmail || '',
            personal_email: hrisEmployee.personalEmail || '',
            status: hrisEmployee.status,
            start_date: hrisEmployee.startDate || undefined,
            remote_created_at: hrisEmployee.remoteCreatedAt || undefined,
            address: hrisEmployee.address && {
              street_1: hrisEmployee.address?.streetAddress1 || '',
              street_2: hrisEmployee.address?.streetAddress2 || '',
              city: hrisEmployee.address?.city || '',
              state: hrisEmployee.address?.state || '',
              country: hrisEmployee.address?.country || '',
              zip_code: hrisEmployee.address?.zip || '',
            },
            termination_date: hrisEmployee.terminationDate || undefined,
          },
          is_onboarding: isOnboarding,
          is_offboarding: isOffboarding,
        },
      }),
      invalidatesTags: (_result, _error, args) => [
        ...invalidatesList(TagTypes.Request),
        ...invalidatesList(TagTypes.Offboarding),
        {
          type: TagTypes.LatestOffboardingByEmployeeId,
          id: args.hrisEmployee.sourceId,
        },
        TagTypes.Employee, // to refresh all employee data (active, inactive, dropdown lists)
      ],
    }),
    fetchADPCredentials: builder.query<boolean, ManageHrisIntegrationArgs>({
      query: args => ({
        url: `/v1/organizations/${args.organizationId}/hris_integrations/${args.hrisIntegrationId}/adp_credentials/fetch`,
        method: 'GET',
      }),
      transformResponse: (response: ADPCredentialsExist) => response.exists,
    }),
    getSyncStatus: builder.query<SyncStatusResponse, GetSyncStatusArgs>({
      query: ({ organizationId, hrisIntegrationId, workflowId }) => ({
        url: `/v2/organizations/${organizationId}/hris_integrations/${hrisIntegrationId}/sync/${workflowId}/status`,
        method: 'GET',
      }),
      transformResponse: (response: WorkflowStatus): SyncStatusResponse => ({
        status: response.status,
        // message field is optional in our frontend type and not provided by this endpoint
        message: undefined,
      }),
    }),
  }),
});

export const {
  useCreateLinkTokenMutation,
  useCreateMergeHrisIntegrationMutation,
  useDeleteMergeHrisIntegrationMutation,
  useUpdateMergeHrisIntegrationMutation,
  useCreateHrisIntegrationV2Mutation,
  useDeleteHrisIntegrationV2Mutation,
  useDisconnectHrisIntegrationMutation,
  useSyncHrisIntegrationMutation,
  useSyncHrisIntegrationV2Mutation,
  useFetchADPCredentialsQuery,
  useListHrisProvidersQuery,
  useDemoSyncHRISEmployeeMutation,
  useGetSyncStatusQuery,
  useLazyGetSyncStatusQuery,
} = hrisIntegrationApiSlice;
